import cx from "classnames";
import { inject, observer } from "mobx-react";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  FaArrowLeft,
  FaArrowRight,
  FaEnvelope,
  FaRedo,
  FaSignInAlt,
  FaSms,
} from "react-icons/fa";
import { Modal } from "reactstrap";
import Button from "../../../components/Button";
import { MessageResultState } from "../../../types/general-types";
import useCountdown from "../../../utils/useCountdown";
import LoginStore from "./store";
import { LoginRequestDto, VerificationTypes } from "./types";

interface IPage {
  LoginStore?: LoginStore;
  clientId: string | null;
  loginRequestDto: LoginRequestDto;
  isShow: boolean;
  toggleModal: any;
  mode?: "login" | "blocked";
  blockedMaskedPhoneNumber?: string | null;
}

const VerifyPage: FC<IPage> = inject("LoginStore")(
  observer((props: IPage) => {
    const {
      LoginStore: store,
      clientId,
      loginRequestDto,
      toggleModal,
      isShow,
      mode = "login",
      blockedMaskedPhoneNumber,
    } = props;
    const { t } = useTranslation();
    const {
      register,
      formState,
      handleSubmit,
      errors,
      control,
      clearErrors,
      setValue,
      getValues,
    } = useForm();

    const [timer, setTimer] = useCountdown();
    const [verificationType, setVerificationType] = useState<VerificationTypes | undefined>(
      mode === "blocked" ? VerificationTypes.Sms : undefined
    );
    const [step, setStep] = useState<"verificationType" | "verificationCode">(
      mode === "blocked" ? "verificationCode" : "verificationType"
    );
    const [isSending, setIsSending] = useState<boolean>(false);
    const isSendingRef = useRef<boolean>(false);
    const verificationCodeRef = useRef<HTMLInputElement | null>(null);
    const blockedAutoSentRef = useRef<boolean>(false);

    const blockedCooldownKey = useMemo(() => {
      const uname = loginRequestDto?.username || "";
      return `blockedSmsCooldown:${uname}`;
    }, [loginRequestDto]);
    const BLOCKED_SMS_COOLDOWN_SECONDS = 180;
    useEffect(() => {
      if (!timer) setValue("verificationCode", "");
    }, [setValue, timer]);

    const sendVerificationCode = useCallback(async () => {
      if (!store || !loginRequestDto) return;
      if (isSendingRef.current) return;
      isSendingRef.current = true;
      setIsSending(true);
      try {
        if (mode === "blocked") {
          const res = await store.SendBlockedUnblockVerificationCode({
            username: loginRequestDto.username,
            password: loginRequestDto.password,
          });
          if (res.state === MessageResultState.SUCCESS) {
            try {
              sessionStorage.setItem(blockedCooldownKey, String(Date.now()));
            } catch {}
            setValue("verificationCode", "");
            setStep("verificationCode");
            setTimer(res.result.verificationSeconds);
          }
          return;
        }

        if (!verificationType) return;
        const res = await store.SendVerificationCode({
          username: loginRequestDto.username,
          password: loginRequestDto.password,
          verificationType: verificationType,
        });
        if (res.state === MessageResultState.SUCCESS) {
          setValue("verificationCode", "");
          setStep("verificationCode");
          setTimer(res.result.verificationSeconds);
        }
      } finally {
        isSendingRef.current = false;
        setIsSending(false);
      }
    }, [
      blockedCooldownKey,
      loginRequestDto,
      mode,
      setTimer,
      setValue,
      store,
      verificationType,
    ]);
    useEffect(() => {
      if (step === "verificationCode" && isShow) {
        setTimeout(() => {
          if (verificationCodeRef.current) {
            verificationCodeRef.current.focus();
          }
        }, 100);
      }
    }, [step, isShow]);

    // Bloke modunda modal açılır açılmaz SMS otomatik gönderilir.
    // sessionStorage'daki son gönderim zamanı 180 sn içindeyse yeni SMS
    // gönderilmez; kalan süre geri sayım olarak gösterilir.
    useEffect(() => {
      if (
        !isShow ||
        mode !== "blocked" ||
        blockedAutoSentRef.current ||
        !loginRequestDto?.username
      ) {
        return;
      }
      blockedAutoSentRef.current = true;

      let remaining = 0;
      try {
        const raw = sessionStorage.getItem(blockedCooldownKey);
        if (raw) {
          const elapsedSec = (Date.now() - parseInt(raw, 10)) / 1000;
          if (
            !Number.isNaN(elapsedSec) &&
            elapsedSec >= 0 &&
            elapsedSec < BLOCKED_SMS_COOLDOWN_SECONDS
          ) {
            remaining = Math.ceil(BLOCKED_SMS_COOLDOWN_SECONDS - elapsedSec);
          }
        }
      } catch {}

      if (remaining > 0) {
        setStep("verificationCode");
        setTimer(remaining);
        return;
      }
      sendVerificationCode();
    }, [
      isShow,
      mode,
      loginRequestDto,
      blockedCooldownKey,
      sendVerificationCode,
      setTimer,
    ]);

    const checkVerificationCode = useCallback(async () => {
      if (store && loginRequestDto && getValues("verificationCode")) {
        if (mode === "blocked") {
          await store
            ?.CheckBlockedUnblockVerificationCode({
              username: loginRequestDto.username,
              password: loginRequestDto.password,
              verificationCode: getValues("verificationCode"),
            })
            .then(async (response) => {
              if (response.state === MessageResultState.SUCCESS) {
                // Bloke kaldırma başarılı olduğunda kullanıcı login ekranına döner
                // ve normal giriş akışını tekrar başlatır.
                toggleModal();
              }
            });
          return;
        }

        if (!verificationType) return;
        await store
          ?.CheckVerificationCode({
            username: loginRequestDto.username,
            password: loginRequestDto.password,
            verificationType: verificationType,
            verificationCode: getValues("verificationCode"),
            clientId: clientId,
          })
          .then((response) => {
            if (response.state === MessageResultState.SUCCESS) {
              toggleModal(response.result);
            }
          });
      }
    }, [
      clientId,
      getValues,
      loginRequestDto,
      mode,
      store,
      toggleModal,
      verificationType,
    ]);

    const VerificationTypePage = useMemo(() => {
      return (
        <>
          <div className="d-flex flex-column align-items-center justify-content-center py-4">
            <div
              className="card w-100 shadow-lg border-0 "
              style={{ maxWidth: "400px", borderRadius: "15px" }}
            >
              <div className="card-body">
                <div className="card-title mb-4 fw-semibold d-flex justify-content-between align-items-center">
                  <h5 className="text-center w-100 m-0">
                    {mode === "blocked" ? t("Blocked Account Verification") : t("Verification Type")}{" "}
                    <span className="text-danger">*</span>
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => toggleModal()}
                  ></button>
                </div>

                {mode === "blocked" && (
                  <div className="alert alert-warning small mb-3" role="alert">
                    {t(
                      "Hesabınız bloke edilmiştir. Lütfen telefonunuza gelen 6 haneli kodu girerek blokeyi kaldırın."
                    )}
                    {blockedMaskedPhoneNumber ? ` (${blockedMaskedPhoneNumber})` : ""}
                  </div>
                )}

                {/* SMS */}
                <div className="form-check mb-3">
                  <input
                    type="radio"
                    name="verificationTypeSms"
                    id="verificationTypeSms"
                    className={"form-check-input"}
                    checked={verificationType === VerificationTypes.Sms}
                    onChange={() => setVerificationType(VerificationTypes.Sms)}
                    style={{ transform: "scale(1.2)" }}
                    disabled={formState.isSubmitting}
                  />
                  <label
                    className="form-check-label align-items-center ms-2"
                    htmlFor="verificationTypeSms"
                    style={{
                      cursor: "pointer",
                      fontSize: "16px",
                      fontWeight: "500",
                      color:
                        verificationType === VerificationTypes.Sms
                          ? "#007bff"
                          : "#555",
                    }}
                  >
                    <FaSms className="me-2" style={{ fontSize: "18px" }} />
                    {t("Sms")}
                  </label>
                </div>

                {mode !== "blocked" && (
                  <div className="form-check mb-4">
                    <input
                      type="radio"
                      name="verificationTypeEmail"
                      id="verificationTypeEmail"
                      className="form-check-input"
                      checked={verificationType === VerificationTypes.Email}
                      onChange={() =>
                        setVerificationType(VerificationTypes.Email)
                      }
                      style={{ transform: "scale(1.2)" }}
                      disabled={formState.isSubmitting}
                    />
                    <label
                      className="form-check-label align-items-center ms-2"
                      htmlFor="verificationTypeEmail"
                      style={{
                        cursor: "pointer",
                        fontSize: "16px",
                        fontWeight: "500",
                        color:
                          verificationType === VerificationTypes.Email
                            ? "#007bff"
                            : "#555",
                        transition: "color 0.3s ease",
                      }}
                    >
                      <FaEnvelope className="me-2" style={{ fontSize: "18px" }} />
                      {t("Email")}
                    </label>
                  </div>
                )}

                <div className="text-center">
                  <Button
                    className="btn btn-primary align-items-center"
                    onClick={handleSubmit(async () => {
                      await sendVerificationCode();
                    })}
                    disabled={
                      isSending ||
                      (mode === "blocked" ? false : !verificationType)
                    }
                    icon={<FaArrowRight className="me-2" />}
                    text={t("Continue")}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      );
    }, [
      formState.isSubmitting,
      handleSubmit,
      sendVerificationCode,
      t,
      toggleModal,
      mode,
      blockedMaskedPhoneNumber,
      verificationType,
      isSending,
    ]);

    const VerificationCodePage = useMemo(() => {
      return (
        <div className="d-flex flex-column align-items-center justify-content-center p-0">
          <div
            className="card w-100 shadow-lg border-0 "
            style={{ maxWidth: "400px", borderRadius: "15px" }}
          >
            <div className="card-body">
              <div className="card-title mb-4 fw-semibold d-flex justify-content-between align-items-center">
                <h5 className="text-center w-100 m-0">
                  {t("Verification Code")}{" "}
                  <span className="text-danger">*</span>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => toggleModal()}
                ></button>
              </div>

              {/* Doğrulama Kodu Input */}
              <div className="mb-4 d-flex justify-content-center">
                <Controller
                  name="verificationCode"
                  control={control}
                  rules={{
                    required: t("Please fill out this field."),
                    minLength: {
                      value: 4,
                      message: t(
                        "The given value must be {{value}} characters..",
                        { value: 4 }
                      ),
                    },
                    maxLength: {
                      value: 8,
                      message: t(
                        "The given value must be {{value}} characters.",
                        { value: 8 }
                      ),
                    },
                  }}
                  render={(field, state) => (
                    <>
                      <input
                        minLength={4}
                        maxLength={8}
                        type="text"
                        placeholder={t("Verification Code")}
                        style={{ width: "50%" }}
                        disabled={formState.isSubmitting || timer === 0 || isSending}
                        {...field}
                        className={cx("form-control text-center ", {
                          "is-invalid": errors["verificationCode"],
                        })}
                        ref={verificationCodeRef}
                        onKeyDown={(
                          event: React.KeyboardEvent<HTMLInputElement>
                        ) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleSubmit(async () => {
                              await checkVerificationCode();
                            })();
                          }
                        }}
                      />
                    </>
                  )}
                />
              </div>

              {/* Zamanlayıcı */}
              <div className="text-center mb-3">
                {isSending ? (
                  <span>{t("Verification code is being sent...")}</span>
                ) : timer && timer > 0 ? (
                  <span>
                    {t("Remaining Time:")} {timer} {t("seconds")}
                  </span>
                ) : (
                  <Button
                    className="btn btn-warning align-items-center"
                    onClick={sendVerificationCode}
                    icon={<FaRedo />}
                    tooltipText={t("Resend Code")}
                    disabled={isSending}
                  />
                )}
              </div>

              <div className="text-center pt-4">
                {mode !== "blocked" && (
                  <Button
                    className="btn btn-primary align-items-center me-4"
                    onClick={async () => {
                      setStep("verificationType");
                    }}
                    icon={<FaArrowLeft className="me-2" />}
                    text={t("Back")}
                    disabled={timer && timer > 0 ? true : false}
                    id="backButton"
                    name="backButton"
                  />
                )}
                <Button
                  className={`btn btn-success align-items-center ${mode !== "blocked" ? "ms-4" : ""}`}
                  // onClick={() => {
                  //   console.log("errors", errors);
                  //   handleSubmit(async () => {
                  //     setStep("verificationType");
                  //   });
                  // }}
                  onClick={handleSubmit(async () => {
                    await checkVerificationCode();
                  })}
                  icon={<FaSignInAlt className="me-2" />}
                  text={t("Verify")}
                  disabled={
                    timer === 0 ||
                    formState.isSubmitting ||
                    isSending ||
                    timer === undefined
                  }
                />
              </div>
            </div>
          </div>
        </div>
      );
    }, [
      checkVerificationCode,
      control,
      errors,
      formState.isSubmitting,
      handleSubmit,
      sendVerificationCode,
      t,
      timer,
      toggleModal,
      mode,
      isSending,
    ]);

    return (
      <Modal
        isOpen={isShow}
        toggle={() => toggleModal()}
        className="verify-modal"
      >
        <div>
          {step === "verificationType"
            ? VerificationTypePage
            : VerificationCodePage}
        </div>
      </Modal>
    );
  })
);
export default VerifyPage;
